import { apiJsonRequest, apiRequest } from './apiClient'

export type PriorAuthDocumentApiField =
  | 'referral'
  | 'records'
  | 'insurance'
  | 'prior_treatment'
  | 'lab_imaging'
  | 'lmn'

export type PriorAuthorizationStatus =
  | 'pending'
  | 'in_review'
  | 'approved'
  | 'denied'
  | 'cancelled'

export const PRIOR_AUTH_STATUS_OPTIONS: { value: PriorAuthorizationStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'cancelled', label: 'Cancelled' },
]

export const labelForPriorAuthStatus = (status: PriorAuthorizationStatus): string =>
  PRIOR_AUTH_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status

export type ApiPriorAuthorization = {
  id?: string
  request_number?: string
  patient_id?: string
  payor_id?: string
  service_code?: string
  service_description?: string
  diagnosis_code?: string
  diagnosis_description?: string
  subscriber_id?: string
  requested_service_date?: string
  submitted_date?: string
  status?: string
  status_label?: string
  provider_name?: string
  provider_npi?: string
  provider_address?: string
} & Record<string, unknown>

export type CreatePriorAuthorizationPayload = {
  request_number: string
  patient_id: string
  payor_id: string
  service_code: string
  service_description: string
  diagnosis_code: string
  diagnosis_description: string
  subscriber_id: string
  requested_service_date: string
  submitted_date: string
  status: PriorAuthorizationStatus
  provider_name: string
  provider_npi: string
  provider_address: string
}

export type PatchPriorAuthorizationStatusPayload = {
  status: PriorAuthorizationStatus
  status_label: string
  description: string
  performed_by_label: string
}

export type PatchPriorAuthorizationPayload = {
  service_code?: string
  service_description?: string
  diagnosis_code?: string
  diagnosis_description?: string
  quantity_length?: string
  requested_service_date?: string
  place_of_service?: string
  rental_purchase?: string
  medical_necessity?: string
  ordering_physician?: string
  face_to_face_date?: string
  urgency?: string
}

export type ApiPriorAuthorizationDocument = {
  id?: string
  document_slot?: string
  name?: string
  file_name?: string
  type?: string
  size?: string | number
  file_size_bytes?: number
  download_url?: string
  uploaded_at?: string
  uploaded_date?: string
  created_at?: string
  uploaded_by?: string
  uploaded_by_label?: string
} & Record<string, unknown>

export type ApiPriorAuthorizationActivity = {
  id?: string
  status?: string
  status_label?: string
  action?: string
  event?: string
  event_type?: string
  description?: string
  details?: string
  performed_by_label?: string
  created_at?: string
  timestamp?: string
  date?: string
} & Record<string, unknown>

export type PriorAuthDetailBundle = {
  priorAuth: ApiPriorAuthorization
  documents: ApiPriorAuthorizationDocument[]
  activity: ApiPriorAuthorizationActivity[]
}

type PriorAuthorizationsListResponse =
  | ApiPriorAuthorization[]
  | {
      data?:
        | ApiPriorAuthorization[]
        | { items?: ApiPriorAuthorization[]; results?: ApiPriorAuthorization[] }
      items?: ApiPriorAuthorization[]
      results?: ApiPriorAuthorization[]
    }

function asPriorAuthorizationsArray(body: PriorAuthorizationsListResponse): ApiPriorAuthorization[] {
  if (Array.isArray(body)) return body
  if (!body || typeof body !== 'object') return []

  if (Array.isArray(body.items)) return body.items
  if (Array.isArray(body.results)) return body.results

  const data = body.data
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    if (Array.isArray(data.items)) return data.items
    if (Array.isArray(data.results)) return data.results
  }
  return []
}

type ListResponse<T> = T[] | ({ data?: T[]; items?: T[] } & Record<string, unknown>)

function asArray<T>(value: ListResponse<T>): T[] {
  if (Array.isArray(value)) return value
  if (Array.isArray(value.data)) return value.data
  if (Array.isArray(value.items)) return value.items
  return []
}

export async function listPriorAuthorizations(): Promise<ApiPriorAuthorization[]> {
  const response = await apiJsonRequest<PriorAuthorizationsListResponse>(
    '/prior-authorizations',
    { method: 'GET' },
    { debugLabel: 'prior-authorizations-list' }
  )
  return asPriorAuthorizationsArray(response)
}

function parsePriorAuthorizationDetail(response: unknown): PriorAuthDetailBundle {
  const raw =
    response && typeof response === 'object' && 'data' in response
      ? ((response as { data?: unknown }).data ?? {})
      : (response ?? {})

  if (!raw || typeof raw !== 'object') {
    return { priorAuth: {}, documents: [], activity: [] }
  }

  const record = raw as Record<string, unknown>
  const documents = Array.isArray(record.documents)
    ? (record.documents as ApiPriorAuthorizationDocument[])
    : []
  const activity = Array.isArray(record.activity)
    ? (record.activity as ApiPriorAuthorizationActivity[])
    : []

  const { documents: _documents, activity: _activity, ...priorAuth } = record
  return {
    priorAuth: priorAuth as ApiPriorAuthorization,
    documents,
    activity,
  }
}

export async function getPriorAuthorization(priorAuthId: string): Promise<PriorAuthDetailBundle> {
  const safeId = encodeURIComponent(priorAuthId)
  const response = await apiJsonRequest<unknown>(
    `/prior-authorizations/${safeId}`,
    { method: 'GET' },
    { debugLabel: 'prior-authorization-detail' }
  )
  return parsePriorAuthorizationDetail(response)
}

export type PriorAuthDocumentUpload = {
  field: PriorAuthDocumentApiField
  file: File
}

function unwrapPriorAuthorizationResponse(
  response: ApiPriorAuthorization | { data?: ApiPriorAuthorization }
): ApiPriorAuthorization {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response.data ?? {}) as ApiPriorAuthorization
  }
  return response as ApiPriorAuthorization
}

async function parsePriorAuthorizationResponse(response: Response): Promise<ApiPriorAuthorization> {
  const text = await response.text()
  if (!response.ok) {
    let message = `Request failed (${response.status})`
    if (text) {
      try {
        const body = JSON.parse(text) as Record<string, unknown>
        const candidate = [body.message, body.msg, body.error, body.detail].find(
          (value) => typeof value === 'string' && value.trim()
        )
        if (typeof candidate === 'string') message = candidate.trim()
      } catch {
        message = text
      }
    }
    throw new Error(message)
  }

  if (!text) return {}
  try {
    return unwrapPriorAuthorizationResponse(JSON.parse(text) as ApiPriorAuthorization | { data?: ApiPriorAuthorization })
  } catch {
    throw new Error('Response returned invalid JSON.')
  }
}

export async function createPriorAuthorization(
  payload: CreatePriorAuthorizationPayload
): Promise<ApiPriorAuthorization> {
  const response = await apiJsonRequest<ApiPriorAuthorization | { data?: ApiPriorAuthorization }>(
    '/prior-authorizations',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  )
  return unwrapPriorAuthorizationResponse(response)
}

export async function createPriorAuthorizationWithDocuments(
  payload: CreatePriorAuthorizationPayload,
  documents: PriorAuthDocumentUpload[]
): Promise<ApiPriorAuthorization> {
  const formData = new FormData()
  formData.append('payload', JSON.stringify(payload))
  for (const { field, file } of documents) {
    formData.append(field, file, file.name)
  }

  const response = await apiRequest('/prior-authorizations', {
    method: 'POST',
    body: formData,
  })
  return parsePriorAuthorizationResponse(response)
}

export async function patchPriorAuthorization(
  priorAuthId: string,
  payload: PatchPriorAuthorizationPayload
): Promise<ApiPriorAuthorization> {
  const safeId = encodeURIComponent(priorAuthId)
  const response = await apiJsonRequest<ApiPriorAuthorization | { data?: ApiPriorAuthorization }>(
    `/prior-authorizations/${safeId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    { debugLabel: 'prior-authorization-update' }
  )
  return unwrapPriorAuthorizationResponse(response)
}

export async function uploadPriorAuthorizationDocuments(
  priorAuthId: string,
  documents: PriorAuthDocumentUpload[]
): Promise<void> {
  if (documents.length === 0) return

  const safeId = encodeURIComponent(priorAuthId)
  const formData = new FormData()
  for (const { field, file } of documents) {
    formData.append(field, file, file.name)
  }

  const response = await apiRequest(
    `/prior-authorizations/${safeId}/documents`,
    { method: 'POST', body: formData },
    'prior-authorization-documents-upload'
  )

  if (!response.ok) {
    const text = await response.text()
    let message = `Request failed (${response.status})`
    if (text) {
      try {
        const body = JSON.parse(text) as Record<string, unknown>
        const candidate = [body.message, body.msg, body.error, body.detail].find(
          (value) => typeof value === 'string' && value.trim()
        )
        if (typeof candidate === 'string') message = candidate.trim()
      } catch {
        message = text
      }
    }
    throw new Error(message)
  }
}

export async function patchPriorAuthorizationStatus(
  priorAuthId: string,
  payload: PatchPriorAuthorizationStatusPayload
): Promise<Record<string, unknown>> {
  const safeId = encodeURIComponent(priorAuthId)
  return apiJsonRequest<Record<string, unknown>>(
    `/prior-authorizations/${safeId}/status`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    { debugLabel: 'prior-authorization-status' }
  )
}

export async function listPriorAuthorizationDocuments(
  priorAuthId: string
): Promise<ApiPriorAuthorizationDocument[]> {
  const response = await apiJsonRequest<ListResponse<ApiPriorAuthorizationDocument>>(
    `/prior-authorizations/${priorAuthId}/documents`,
    {
      method: 'GET',
    }
  )
  return asArray(response)
}

export async function listPriorAuthorizationActivity(
  priorAuthId: string
): Promise<ApiPriorAuthorizationActivity[]> {
  const response = await apiJsonRequest<ListResponse<ApiPriorAuthorizationActivity>>(
    `/prior-authorizations/${priorAuthId}/activity`,
    {
      method: 'GET',
    }
  )
  return asArray(response)
}
